import { stringify } from 'query-string';
import { saveAs } from 'file-saver';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  DocumentInfoResponse,
  HistoryFilters,
  HistoryResponse,
  UploadResponse,
  VerifyDocumentRequest,
  VerifyResponse,
} from '../interfaces';
import Axios from '../utils/axios-config';
import { VerifyDocumentResponse } from '../interfaces/api-response';
import { globalConfig } from '../configuration/config';
import { extractFilenameFromHeader } from '../utils/utils';

export const useDocument = () => {
  const uploadDocument = async (
    payload: FormData,
  ): Promise<{ data: UploadResponse }> =>
    await Axios.post<UploadResponse>(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/upload`,
      payload,
    );

  const getDocumentById = (id: string): Promise<AxiosResponse> =>
    Axios.get(`${globalConfig.config.REACT_APP_API_URL}/pdf/${id}`, {
      responseType: 'blob',
    });

  const downloadFile = async (id: string): Promise<void> =>
    getDocumentById(id).then(({ data, headers }) => {
      const contentDisposition =
        headers['content-disposition'] || headers['Content-Disposition'];

      if (contentDisposition) {
        const filename = extractFilenameFromHeader(contentDisposition);
        saveAs(data, filename);
      }
    });

  const verifySignature = (
    payload: VerifyDocumentRequest,
    config?: AxiosRequestConfig,
  ): Promise<VerifyResponse> =>
    Axios.post<VerifyDocumentResponse>(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/verifySignature`,
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...config,
      },
    );

  const cancelDocumentSign = (id: string) =>
    Axios.delete(`${globalConfig.config.REACT_APP_API_URL}/pdf/${id}/cancel`);

  const getDocumentInfo = (
    id: string,
  ): Promise<{ data: DocumentInfoResponse }> =>
    Axios.get(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/${id}/documentInfo`,
    );

  const getHistory = (
    filters: HistoryFilters,
  ): Promise<{ data: HistoryResponse }> =>
    Axios.get(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/history?${stringify(
        filters,
      )}`,
    );

  const renewOtpCode = (id: string): Promise<{ data: string }> =>
    Axios.get(`${globalConfig.config.REACT_APP_API_URL}/pdf/${id}/otpRenew`);

  const archiveDocument = (id: string) =>
    Axios.patch(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/${id}/archive?isArchived=true`,
    );

  const restoreDocument = (id: string) =>
    Axios.patch(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/${id}/archive?isArchived=false`,
    );

  const archiveMultipleDocuments = (documentIds: string[]) =>
    Axios.patch(
      `${globalConfig.config.REACT_APP_API_URL}/pdf/archive/batch`,
      documentIds,
    );

  const deleteMultipleDocuments = (documentIds: string[]) =>
    Axios.delete(`${globalConfig.config.REACT_APP_API_URL}/pdf`, {
      data: documentIds,
    });

  const getCertificatesIssuer = (): Promise<{ data: { issuer: string } }> =>
    Axios.get(`${globalConfig.config.REACT_APP_API_URL}/certificates/issuer`);

  const saveCurrentSignature = async (
    payload: FormData,
  ): Promise<{ data: UploadResponse }> =>
    await Axios.put<UploadResponse>(
      `${globalConfig.config.REACT_APP_API_URL}/signatureImage`,
      payload,
    );

  const deleteDefaultSignature = () =>
    Axios.delete(`${globalConfig.config.REACT_APP_API_URL}/signatureImage`);

  const getSignatureImage = (): Promise<AxiosResponse> =>
    Axios.get(`${globalConfig.config.REACT_APP_API_URL}/signatureImage`, {
      responseType: 'blob',
    });

  const getMetadataSignature = (): Promise<{ data: any }> =>
    Axios.get(
      `${globalConfig.config.REACT_APP_API_URL}/signatureImage/metadata`,
    );

  return {
    uploadDocument,
    getDocumentById,
    downloadFile,
    verifySignature,
    cancelDocumentSign,
    getDocumentInfo,
    getHistory,
    renewOtpCode,
    archiveDocument,
    restoreDocument,
    archiveMultipleDocuments,
    deleteMultipleDocuments,
    getCertificatesIssuer,
    saveCurrentSignature,
    deleteDefaultSignature,
    getSignatureImage,
    getMetadataSignature,
  };
};
